import { DownloadOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Collapse, InputNumber, Progress, Select, Space, Typography, message } from "antd";
import { useMemo, useRef, useState } from "react";
import {
  AdUserOpenIdExportData,
  AdUserOpenIdExportRule,
  AdUserOpenIdExportRules,
  AdUserOpenIdExportType,
  ComparisonOperator,
} from "../../shared/protocols/editor/PtlCommercialAnalyticsQuery";
import { Section, SectionHeader } from "./commercialAnalyticsStyles";
import { downloadTextFile, queryCommercialAnalytics } from "./commercialAnalyticsUtils";

const DEFAULT_RULES: AdUserOpenIdExportRules = {
  lowQuality: {
    rewardedCompleted: { operator: "eq", value: 0 },
    interstitialCompleted: { operator: "lt", value: 5 },
    relation: "and",
  },
  highQuality: {
    rewardedCompleted: { operator: "gte", value: 1 },
    interstitialCompleted: { operator: "gte", value: 20 },
    relation: "or",
  },
};

const OPERATOR_OPTIONS: Array<{ label: string; value: ComparisonOperator }> = [
  { label: "=", value: "eq" },
  { label: "<", value: "lt" },
  { label: "<=", value: "lte" },
  { label: ">", value: "gt" },
  { label: ">=", value: "gte" },
];

const RELATION_OPTIONS = [
  { label: "同时满足", value: "and" },
  { label: "满足任一", value: "or" },
];

interface ExportProgress {
  running: boolean;
  percent: number;
  step: string;
}

export default function AdUserOpenIdExportPanel() {
  const [rules, setRules] = useState<AdUserOpenIdExportRules>(DEFAULT_RULES);
  const [progress, setProgress] = useState<ExportProgress>({ running: false, percent: 0, step: "未开始" });
  const progressTimer = useRef<number>();

  const ruleDescriptions = useMemo(() => ({
    lowQuality: describeRule(rules.lowQuality),
    highQuality: describeRule(rules.highQuality),
  }), [rules]);

  const exportOpenIds = async (exportType: AdUserOpenIdExportType) => {
    if (progress.running) return;
    window.clearInterval(progressTimer.current);
    setProgress({ running: true, percent: 8, step: "准备导出规则" });
    progressTimer.current = window.setInterval(() => {
      setProgress((prev) => prev.running
        ? { ...prev, percent: Math.min(88, prev.percent + 3), step: "查询用户与广告统计" }
        : prev);
    }, 400);

    try {
      setProgress({ running: true, percent: 20, step: "提交导出请求" });
      const data = await queryCommercialAnalytics({
        timestamp: Date.now(),
        token: localStorage.getItem("token") || "",
        scope: "adUserOpenIdExport",
        kind: "exportOpenIds",
        exportType,
        exportRules: rules,
      });
      if (data.scope !== "adUserOpenIdExport") {
        throw new Error("导出接口返回数据类型错误");
      }
      const exportData = data as AdUserOpenIdExportData;
      setProgress({ running: true, percent: 96, step: `生成 CSV 文件：${exportData.count} 个 OpenID` });
      downloadTextFile(exportData.filename, exportData.csv, "text/csv;charset=utf-8", { bom: true });
      setProgress({ running: false, percent: 100, step: `导出完成：${exportData.count} 个 OpenID` });
      message.success(`已导出 ${exportData.count} 个 OpenID`);
    } catch (error: any) {
      setProgress({ running: false, percent: 0, step: error?.message || "导出失败" });
    } finally {
      window.clearInterval(progressTimer.current);
    }
  };

  return (
    <Section>
      <SectionHeader>
        <span>用户 OpenID 导出</span>
        <Space wrap>
          <Button
            icon={<DownloadOutlined />}
            loading={progress.running}
            onClick={() => exportOpenIds("lowQuality")}
          >
            导出低质用户ID
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={progress.running}
            onClick={() => exportOpenIds("highQuality")}
          >
            导出优质用户ID
          </Button>
        </Space>
      </SectionHeader>

      <Typography.Text type="secondary">
        低质默认：未完播激励广告，且完播插屏广告少于 5 个。优质默认：完播激励广告不少于 1 个，或完播插屏广告不少于 20 个。
        OpenID 仅导出非空、非 test 开头、非下划线开头的数据。
      </Typography.Text>

      <div style={{ marginTop: 10 }}>
        <Progress percent={progress.percent} size="small" status={progress.running ? "active" : undefined} />
        <Typography.Text type={progress.running ? "secondary" : undefined}>{progress.step}</Typography.Text>
      </div>

      <Collapse
        size="small"
        style={{ marginTop: 10 }}
        items={[
          {
            key: "rules",
            label: <><SettingOutlined /> 导出规则配置</>,
            children: (
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <RuleEditor
                  title="低质用户"
                  rule={rules.lowQuality}
                  description={ruleDescriptions.lowQuality}
                  onChange={(rule) => setRules((prev) => ({ ...prev, lowQuality: rule }))}
                />
                <RuleEditor
                  title="优质用户"
                  rule={rules.highQuality}
                  description={ruleDescriptions.highQuality}
                  onChange={(rule) => setRules((prev) => ({ ...prev, highQuality: rule }))}
                />
              </Space>
            ),
          },
        ]}
      />
    </Section>
  );
}

interface RuleEditorProps {
  title: string;
  rule: AdUserOpenIdExportRule;
  description: string;
  onChange: (rule: AdUserOpenIdExportRule) => void;
}

function RuleEditor({ title, rule, description, onChange }: RuleEditorProps) {
  const update = (patch: Partial<AdUserOpenIdExportRule>) => onChange({ ...rule, ...patch });
  return (
    <div style={{ padding: 10, border: "1px solid #edf1f5", borderRadius: 8 }}>
      <Typography.Text strong>{title}</Typography.Text>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, alignItems: "center" }}>
        <span>激励完播</span>
        <Select
          value={rule.rewardedCompleted.operator}
          options={OPERATOR_OPTIONS}
          style={{ width: 76 }}
          onChange={(operator) => update({ rewardedCompleted: { ...rule.rewardedCompleted, operator } })}
        />
        <InputNumber
          min={0}
          precision={0}
          value={rule.rewardedCompleted.value}
          onChange={(value) => update({ rewardedCompleted: { ...rule.rewardedCompleted, value: Number(value || 0) } })}
        />
        <Select
          value={rule.relation}
          options={RELATION_OPTIONS}
          style={{ width: 104 }}
          onChange={(relation) => update({ relation })}
        />
        <span>插屏完播</span>
        <Select
          value={rule.interstitialCompleted.operator}
          options={OPERATOR_OPTIONS}
          style={{ width: 76 }}
          onChange={(operator) => update({ interstitialCompleted: { ...rule.interstitialCompleted, operator } })}
        />
        <InputNumber
          min={0}
          precision={0}
          value={rule.interstitialCompleted.value}
          onChange={(value) => update({ interstitialCompleted: { ...rule.interstitialCompleted, value: Number(value || 0) } })}
        />
      </div>
      <Typography.Text type="secondary">{description}</Typography.Text>
    </div>
  );
}

function describeRule(rule: AdUserOpenIdExportRule): string {
  const relation = rule.relation === "or" ? "或" : "且";
  return `激励完播 ${operatorText(rule.rewardedCompleted.operator)} ${rule.rewardedCompleted.value} ${relation} 插屏完播 ${operatorText(rule.interstitialCompleted.operator)} ${rule.interstitialCompleted.value}`;
}

function operatorText(operator: ComparisonOperator): string {
  return OPERATOR_OPTIONS.find((option) => option.value === operator)?.label || operator;
}
